import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts';
import { useLocation } from 'react-router-dom';
import {
	getDashboardKpis,
	getRevenueTrend,
	getOrderModeSplit,
	getPaymentMethodSplit,
	getAggregatorSplit,
	getBrandSplit,
	getPeakHours,
	getTopMenuItems,
	getTopOutlets,
	getTopCustomers,
} from '../services/company_panel/dashboardServices'
import { companyDetail, endOfDay, showPrice, startOfDay } from '../helpers/commonFunctions'
import CustomRangepicker from '../components/company_panel/common/rangepicker'
import DashboardList from './dashboard/list'
import { SOCKET_CLIENT } from '../utils/socket'
import { SOCKET_EVENTS } from '../utils/Constants'

// ---- Live updates -----------------------------------------------------------
// The day the trend API puts a business_date in: its own date, as the database stores it
// (to_char(business_date::date)), so a live order lands on the point the API would have.
const toApiDay = (value) => new Date(value).toISOString().slice(0, 10);

// same rule as the /kpis API: no badge when the previous period is empty
const pctDelta = (curr, prev) => {
	if (prev === null || prev === undefined || Number(prev) === 0) return null;
	return +(((curr - prev) / prev) * 100).toFixed(2);
};

const sameId = (a, b) => String(a ?? '') === String(b ?? '');

// Every donut slice is drawn at least this share of the ring, so a tender (or order mode,
// or brand) with a small amount next to a very large one is still there to see and hover.
// Only the drawing uses the raised sizes; percentages, tooltips and the total are real.
const MIN_SLICE_SHARE = 0.02;
const toSliceSizes = (values) => {
	const total = values.reduce((sum, value) => sum + Math.max(0, value), 0);
	return values.map(value => (value > 0 ? Math.max(value, total * MIN_SLICE_SHARE) : 0));
};

// Hovering a legend name does what hovering its slice does: ApexCharts lights the slice and
// shows its tooltip (its own legend hover only fades the other slices, and is turned off in
// buildDonut). The pointer is over the legend, so the tooltip is placed at the middle of
// the slice's arc instead. Wired once per chart from its mounted event.
const linkLegendHoverToSlices = (chart) => {
	const root = chart?.el;
	if (!root || root.dataset.legendHoverLinked) return;
	root.dataset.legendHoverLinked = 'true';
	let active = null;

	// keepTooltip: moving straight to another name only moves the highlight. Hiding the
	// tooltip first would make ApexCharts (which updates it at most every 20ms) hold back
	// showing the next one.
	const leave = (keepTooltip = false) => {
		if (!active) return;
		const { path, group } = active;
		active = null;
		path.dispatchEvent(new MouseEvent('mouseleave'));
		if (!keepTooltip) group.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
	};

	// rel is the slice's 1-based position, as ApexCharts numbers legend items and slices
	const enter = (rel) => {
		const group = root.querySelector(`.apexcharts-pie-series[rel='${rel}']`);
		const path = group?.querySelector('path');
		const ring = root.querySelector('.apexcharts-slices')?.getBoundingClientRect();
		if (!path || !ring) return;
		// halfway round the slice's arc, halfway across the ring; angles start at 12 o'clock
		const { series } = chart.w.globals;
		const { startAngle = 0, donut } = chart.w.config.plotOptions.pie;
		const total = series.reduce((sum, value) => sum + value, 0) || 1;
		const before = series.slice(0, rel - 1).reduce((sum, value) => sum + value, 0);
		const angle = ((startAngle + ((before + series[rel - 1] / 2) / total) * 360) * Math.PI) / 180;
		const outer = ring.width / 2;
		const radius = (outer * (1 + (parseFloat(donut?.size) || 65) / 100)) / 2;
		const point = {
			clientX: ring.left + outer + radius * Math.sin(angle),
			clientY: ring.top + ring.height / 2 - radius * Math.cos(angle),
		};
		active = { rel, path, group, point };
		// ApexCharts places the tooltip from the last pointer position it saw
		chart.w.globals.clientX = point.clientX;
		chart.w.globals.clientY = point.clientY;
		path.dispatchEvent(new MouseEvent('mouseenter', point));
		path.dispatchEvent(new MouseEvent('mousemove', { ...point, bubbles: true }));
	};

	root.addEventListener('mouseover', (e) => {
		const item = e.target.closest?.('.apexcharts-legend-series');
		const rel = item ? Number(item.getAttribute('rel')) : null;
		if (rel === (active ? active.rel : null)) return;
		leave(Boolean(active && rel));
		if (rel) enter(rel);
	});
	root.addEventListener('mouseleave', () => leave());
	// ApexCharts saves every pointer move inside the chart, legend included, and a tooltip
	// update it held back reads the latest one. This listener runs after ApexCharts' (the
	// move bubbles up to here), so while a name is hovered the slice's point is put back.
	root.addEventListener('mousemove', () => {
		if (!active) return;
		chart.w.globals.clientX = active.point.clientX;
		chart.w.globals.clientY = active.point.clientY;
	});
};

// distinct enough that legend entries don't repeat a colour until the 13th slice
const DONUT_COLORS = ['#2F9BFF', '#FF7A59', '#22c55e', '#a855f7', '#eab308', '#ef4444', '#06b6d4', '#ec4899', '#6366f1', '#84cc16', '#64748b', '#92400e'];

const orderText = (count) => `${count} ${count === 1 ? 'order' : 'orders'}`;

// The middle of a donut: the orders, the amount, and "SAR" on its own line under it (a line
// in the amount's text element, as ApexCharts draws only two). ApexCharts leaves the middle
// alone on hover because total.showAlways is set, so it's written here, after every draw with
// the totals and on hover with the hovered slice's numbers.
const setDonutCenter = (chart, count, revenue) => {
	const label = chart?.el?.querySelector('.apexcharts-datalabel-label');
	const value = chart?.el?.querySelector('.apexcharts-datalabel-value');
	if (label) label.textContent = orderText(count);
	if (!value) return;
	value.textContent = showPrice(revenue);
	const unit = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
	unit.setAttribute('x', value.getAttribute('x'));
	unit.setAttribute('dy', '1.2em');
	unit.textContent = 'SAR';
	value.appendChild(unit);
};

// A donut's options and series. Slices are sized by one measure (sizeBy: 'count' or
// 'revenue'), drawn as toSliceSizes of it. The tooltip shows the slice's name and its share
// of that measure; the middle shows the hovered slice's orders and revenue (from the slice or
// its legend name), and the totals otherwise. For the payment methods the API's count is
// payments, so a split-bill order paid by two tenders is counted in both slices.
// the donuts' height; the ring is sized to fit it
const DONUT_HEIGHT = 360;

// chart root -> the slice under the pointer, so the middle can be written again for it
const hoveredSlice = new WeakMap();

const buildDonut = (dataRef) => {
	const sum = (list) => list.reduce((total, value) => total + value, 0);
	const share = (index) => {
		const { counts, revenues, sizeBy } = dataRef.current;
		const values = sizeBy === 'count' ? counts : revenues;
		const totalValue = sum(values);
		const pct = totalValue > 0 ? (values[index] / totalValue) * 100 : 0;
		return pct > 0 && pct < 0.1 ? '<0.1%' : `${pct.toFixed(1)}%`;
	};
	// the hovered slice's numbers, or the totals
	const writeCenter = (chart) => {
		const { counts, revenues } = dataRef.current;
		const index = hoveredSlice.get(chart?.el);
		if (index != null && index < counts.length) setDonutCenter(chart, counts[index], revenues[index]);
		else setDonutCenter(chart, sum(counts), sum(revenues));
	};
	// ApexCharts puts its own text back in the middle on a slice's mouseleave and on a click,
	// after these events run, so the middle is written again once it's done
	const writeCenterAfterApexCharts = (chart) => setTimeout(() => writeCenter(chart), 0);
	return {
		chart: {
			type: 'donut',
			height: DONUT_HEIGHT,
			events: {
				mounted: (chart) => { linkLegendHoverToSlices(chart); writeCenter(chart); },
				updated: writeCenter,
				dataPointMouseEnter: (event, chart, { dataPointIndex }) => {
					hoveredSlice.set(chart.el, dataPointIndex);
					writeCenter(chart);
				},
				dataPointMouseLeave: (event, chart) => {
					hoveredSlice.delete(chart.el);
					writeCenterAfterApexCharts(chart);
				},
				dataPointSelection: (event, chart) => writeCenterAfterApexCharts(chart),
			},
		},
		labels: dataRef.current.labels,
		colors: DONUT_COLORS,
		legend: { position: 'bottom', onItemHover: { highlightDataSeries: false } },
		dataLabels: { enabled: true, formatter: (val, { seriesIndex }) => share(seriesIndex) },
		tooltip: { y: { formatter: (v, { seriesIndex }) => share(seriesIndex) } },
		plotOptions: {
			pie: {
				donut: {
					labels: {
						show: true,
						// raised from -10 / 10 so the three lines, with SAR under the amount, sit centred
						name: { offsetY: -22 },
						value: { offsetY: -2 },
						total: {
							show: true,
							showAlways: true,
							label: orderText(sum(dataRef.current.counts)),
							// setDonutCenter rewrites both lines (and adds SAR) once the chart is drawn
							formatter: () => showPrice(sum(dataRef.current.revenues)),
						},
					},
				},
			},
		},
	};
};

// A donut's options keep one identity while only its numbers change, so react-apexcharts
// calls updateSeries, which redraws the slices. With new options it calls updateOptions
// instead, and the old slices stay on screen until something repaints them, such as a hover.
// Labels changing (a slice added or removed) does rebuild the options.
const useDonut = ({ labels, counts, revenues, sizeBy }) => {
	const dataRef = useRef({ labels, counts, revenues, sizeBy });
	dataRef.current = { labels, counts, revenues, sizeBy };
	// rebuilt when the slice names change, the one case the options themselves differ; the
	// numbers are read from the ref, which is why the linter can't see the dependency
	const labelsKey = labels.join('|');
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const options = useMemo(() => buildDonut(dataRef), [labelsKey]);
	return { options, series: toSliceSizes(sizeBy === 'count' ? counts : revenues) };
};
const addMoney = (a, b) => +(Number(a || 0) + Number(b || 0)).toFixed(2);

const Dashboard = () => {
	// Everything follows the date range picked; the dashboard opens on today, and Reset
	// goes back to it. todayPick is the day the picker shows by default: a new one on each
	// reset, which also makes the picker show it again (and keeps "today" right after midnight).
	const [todayPick, setTodayPick] = useState(() => { const now = new Date(); return [now, now]; });
	const [range, setRange] = useState(() => ({ start: todayPick[0], end: todayPick[1] }));
	const resetToToday = () => {
		const now = new Date();
		setTodayPick([now, now]);
		setRange({ start: now, end: now });
	};

	// Clicking Dashboard in the sidebar (or the logo) while already here doesn't remount the
	// page, so it goes back to today here: each click is a new location with a new key.
	const location = useLocation();
	const firstLocationKeyRef = useRef(location.key);
	useEffect(() => {
		if (location.key === firstLocationKeyRef.current) return;
		resetToToday();
	}, [location.key]);

	const [kpis, setKpis] = useState(null);
	const [kpiLoader, setKpiLoader] = useState(true);

	const [trendSeries, setTrendSeries] = useState([
		{ name: 'Revenue (SAR)', data: [] },
		{ name: 'Orders', data: [] },
	]);
	const [trendCategories, setTrendCategories] = useState([]);
	const [trendLoader, setTrendLoader] = useState(true);

	const [orderModeRows, setOrderModeRows] = useState([]);
	const [orderModeLoader, setOrderModeLoader] = useState(true);

	const [paymentMethodRows, setPaymentMethodRows] = useState([]);
	const [paymentMethodLoader, setPaymentMethodLoader] = useState(true);

	const [aggregatorRows, setAggregatorRows] = useState([]);
	const [aggregatorLoader, setAggregatorLoader] = useState(true);

	const [brandRows, setBrandRows] = useState([]);
	const [brandLoader, setBrandLoader] = useState(true);

	const [peakHoursRows, setPeakHoursRows] = useState([]);
	const [peakHoursLoader, setPeakHoursLoader] = useState(true);

	const [topMenuItems, setTopMenuItems] = useState([]);
	const [topMenuItemsLoader, setTopMenuItemsLoader] = useState(true);

	const [topOutlets, setTopOutlets] = useState([]);
	const [topOutletsLoader, setTopOutletsLoader] = useState(true);

	const [topCustomers, setTopCustomers] = useState([]);
	const [topCustomersLoader, setTopCustomersLoader] = useState(true);

	// The whole of both days, as ISO timestamps. The badges compare the range with the one
	// just before it (for a single day, the day before).
	const buildPayload = () => ({
		start_date: startOfDay(range.start).toISOString(),
		end_date: endOfDay(range.end).toISOString(),
	});

	// ---- Loading by section ---------------------------------------------------
	// A widget's API is called only once its section is on (or near) the screen. Each
	// widget remembers the range its data is for, so a filter change reloads the sections
	// in view and leaves the rest until they're scrolled to.
	const rangeKey = (payload) => (payload ? `${payload.start_date}_${payload.end_date}` : '');

	// Read by the observer and socket handlers, which are registered once.
	const payloadRef = useRef(null);
	const trendCategoriesRef = useRef([]);
	const visibleSectionsRef = useRef(new Set());
	// widget -> the range key its data is for
	const loadedRangeRef = useRef({});
	// widget -> loads of it still running
	const inFlightRef = useRef({});
	// the orders / users list at the bottom mounts (and loads) the first time it's reached
	const [showRecentLists, setShowRecentLists] = useState(false);

	const rowsOf = (data) => data?.data?.response?.rows || [];

	// Every widget: the section it sits in, its API, its loader flag and where the response goes.
	const WIDGETS = {
		kpis: { section: 'kpis', request: getDashboardKpis, setLoader: setKpiLoader, apply: (data) => setKpis(data?.data?.response || null) },
		trend: {
			section: 'trend', request: getRevenueTrend, setLoader: setTrendLoader,
			apply: (data) => {
				const series = data?.data?.response?.series || [];
				const categories = series.map(r => r.business_date);
				trendCategoriesRef.current = categories;
				setTrendCategories(categories);
				setTrendSeries([
					{ name: 'Revenue (SAR)', type: 'area', data: series.map(r => Number(r.revenue || 0)) },
					{ name: 'Orders', type: 'line', data: series.map(r => Number(r.orders || 0)) },
				]);
			},
		},
		orderModes: { section: 'splits', request: getOrderModeSplit, setLoader: setOrderModeLoader, apply: (data) => setOrderModeRows(rowsOf(data)) },
		paymentMethods: { section: 'splits', request: getPaymentMethodSplit, setLoader: setPaymentMethodLoader, apply: (data) => setPaymentMethodRows(rowsOf(data)) },
		aggregators: { section: 'splits', request: getAggregatorSplit, setLoader: setAggregatorLoader, apply: (data) => setAggregatorRows(rowsOf(data)) },
		brands: { section: 'brands', request: getBrandSplit, setLoader: setBrandLoader, apply: (data) => setBrandRows(rowsOf(data)) },
		peakHours: { section: 'peakHours', request: getPeakHours, setLoader: setPeakHoursLoader, apply: (data) => setPeakHoursRows(rowsOf(data)) },
		topMenuItems: { section: 'topLists', request: getTopMenuItems, setLoader: setTopMenuItemsLoader, apply: (data) => setTopMenuItems(rowsOf(data)) },
		topOutlets: { section: 'topLists', request: getTopOutlets, setLoader: setTopOutletsLoader, apply: (data) => setTopOutlets(rowsOf(data)) },
		topCustomers: { section: 'topLists', request: getTopCustomers, setLoader: setTopCustomersLoader, apply: (data) => setTopCustomers(rowsOf(data)) },
	};

	// A response for a range the filters have since moved off is dropped.
	const loadWidget = (widget) => {
		const { request, setLoader, apply } = WIDGETS[widget];
		const payload = payloadRef.current;
		const key = rangeKey(payload);
		const isCurrent = () => rangeKey(payloadRef.current) === key;
		inFlightRef.current[widget] = (inFlightRef.current[widget] || 0) + 1;
		setLoader(true);
		return request(payload).then(data => {
			if (isCurrent()) apply(data);
		}).catch(err => {
			console.log(`${widget} error ====> `, err);
		}).finally(() => {
			inFlightRef.current[widget] -= 1;
			if (isCurrent()) {
				setLoader(false);
				// a failed load counts as loaded too, so it isn't retried in a loop
				loadedRangeRef.current[widget] = key;
			}
			// a filter change while this was loading couldn't start this widget's load for the
			// new range, so it starts now (nothing loads when the range hasn't changed)
			if (!inFlightRef.current[widget]) loadVisibleWidgetsRef.current();
		});
	};

	// Loads each widget of a visible section that has no data yet for the current range.
	// This and a filter change are the only times an API is called.
	const loadVisibleWidgets = () => {
		const key = rangeKey(payloadRef.current);
		if (!key) return;
		Object.keys(WIDGETS).forEach(widget => {
			if (!visibleSectionsRef.current.has(WIDGETS[widget].section) || inFlightRef.current[widget]) return;
			if (loadedRangeRef.current[widget] !== key) loadWidget(widget);
		});
	};

	useEffect(() => {
		payloadRef.current = buildPayload();
		loadVisibleWidgets();
	}, [range]);

	// Sections are marked with data-dashboard-section. 200px of margin starts a load just
	// before the section scrolls into view.
	useEffect(() => {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				const section = entry.target.dataset.dashboardSection;
				if (entry.isIntersecting) visibleSectionsRef.current.add(section);
				else visibleSectionsRef.current.delete(section);
				if (section === 'recent' && entry.isIntersecting) setShowRecentLists(true);
			});
			loadVisibleWidgetsRef.current();
		}, { rootMargin: '200px 0px' });
		document.querySelectorAll('[data-dashboard-section]').forEach(el => observer.observe(el));
		return () => observer.disconnect();
	}, []);

	// One dashboard_order_updated event per paid order and per refund. Every number follows
	// the dashboard APIs' own rules (they sum paid and refunded transactions), so the
	// screen shows what a reload would.
	const applyDashboardEvent = (event) => {
		const payload = payloadRef.current;
		if (!payload || !event?.business_date) return;
		const paid = event.action === 'paid';
		const refunded = event.action === 'refunded';
		if (!paid && !refunded) return;

		// The APIs filter business_date between the two timestamps sent, so the event's own
		// business_date is compared the same way: in range when the API would have counted it.
		const businessDateAt = new Date(event.business_date).getTime();
		if (!(businessDateAt >= Date.parse(payload.start_date) && businessDateAt <= Date.parse(payload.end_date))) return;

		// A widget is patched only when its data is for this range and it isn't loading: that
		// load may already include the event. One not loaded yet includes it when it loads.
		const key = rangeKey(payload);
		const canPatch = (widget) => !inFlightRef.current[widget] && loadedRangeRef.current[widget] === key;
		// Applies update to the row isMatch finds. Events never call an API: the event carries
		// ids, not names, so a row the screen doesn't have yet can't be added here and shows
		// on the widget's next load instead.
		const patchRows = (isMatch, update) => (rows) => rows.map(row => (isMatch(row) ? update(row) : row));

		const refundedAmount = Number(event.refunded_amount || 0);
		// revenue is amount - refunded_amount; a refund keeps the order counted
		const revenue = paid ? Number(event.amount || 0) - refundedAmount : -refundedAmount;
		const orders = paid ? 1 : 0;

		if (canPatch('kpis')) {
			setKpis(prev => {
				if (!prev) return prev;
				const next = {
					...prev,
					revenue: addMoney(prev.revenue, revenue),
					orders: Number(prev.orders || 0) + orders,
					refund_count: Number(prev.refund_count || 0) + (refunded ? 1 : 0),
					refund_amount: addMoney(prev.refund_amount, refunded ? refundedAmount : 0),
				};
				next.avg_order_value = next.orders > 0 ? +(next.revenue / next.orders).toFixed(2) : 0;
				// the % badges can follow only when /kpis also returns the previous period's values
				if (prev.previous) {
					next.deltas = {
						revenue_pct: pctDelta(next.revenue, prev.previous.revenue),
						orders_pct: pctDelta(next.orders, prev.previous.orders),
						aov_pct: pctDelta(next.avg_order_value, prev.previous.avg_order_value),
						refund_pct: pctDelta(next.refund_count, prev.previous.refund_count),
					};
				}
				return next;
			});
		}

		const dayIndex = trendCategoriesRef.current.indexOf(toApiDay(event.business_date));
		if (dayIndex !== -1 && canPatch('trend')) {
			setTrendSeries(prev => prev.map((series, seriesIndex) => ({
				...series,
				data: series.data.map((value, i) => {
					if (i !== dayIndex) return value;
					return seriesIndex === 0 ? addMoney(value, revenue) : value + orders;
				}),
			})));
		}

		// Brand donut: a paid order adds an order and its revenue to its brand's slice, a refund
		// takes the refunded amount off it (the order stays counted, as in the API). A brand the
		// donut doesn't show yet has no name in the event, so it shows on the next load.
		if (event.brand_id != null && canPatch('brands')) {
			setBrandRows(patchRows(
				(row) => sameId(row.brand_id, event.brand_id),
				(row) => ({ ...row, orders: Number(row.orders || 0) + orders, revenue: addMoney(row.revenue, revenue) }),
			));
		}

		// Top Outlets: a paid order adds an order and its revenue to its outlet, a refund takes
		// the refunded amount off it (the order stays counted, as in the API). An outlet not in
		// the top 10 shows on the next load.
		if (event.outlet_id != null && canPatch('topOutlets')) {
			setTopOutlets(rows => rows
				.map(row => (sameId(row.outlet_id, event.outlet_id)
					? { ...row, orders: Number(row.orders || 0) + orders, revenue: addMoney(row.revenue, revenue) }
					: row))
				.sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0)));
		}

		// the peak hours API buckets created_at in the database session's timezone, UTC;
		// a refund leaves the hour's count as it is
		const hour = paid ? new Date(event.created_at).getUTCHours() : NaN;
		if (!isNaN(hour) && canPatch('peakHours')) {
			setPeakHoursRows(patchRows(
				(row) => Number(row.hour) === hour,
				(row) => ({ ...row, orders: Number(row.orders || 0) + 1 }),
			));
		}

		// The event carries no order mode, tender, aggregator, items or customer, so those three
		// donuts, Top Menu Items and Top Customers change on their next load.
	};

	// the latest render's functions, for the observer and socket handlers registered once
	const loadVisibleWidgetsRef = useRef(loadVisibleWidgets);
	const applyDashboardEventRef = useRef(applyDashboardEvent);
	loadVisibleWidgetsRef.current = loadVisibleWidgets;
	applyDashboardEventRef.current = applyDashboardEvent;

	// The company's dashboard room: the backend emits dashboard_order_updated there for each
	// paid order and each refund, delivered on GET-DATA like every other socket event.
	useEffect(() => {
		// the company's register id (company_detail), as the backend's company_register_id rooms use,
		// not company_user.company_id, which is the companies row id
		const companyId = companyDetail()?.company_id;
		if (!companyId) return;
		const roomID = `dashboard-${companyId}`;

		const joinRoom = () => SOCKET_CLIENT.emit('JOIN-USER', { roomID });
		const handleData = (response) => {
			if (response?.event !== SOCKET_EVENTS.DASHBOARD_ORDER_UPDATED) return;
			applyDashboardEventRef.current(response?.data);
		};

		// join now (socket.io buffers the emit until connected) and again on every reconnect
		joinRoom();
		SOCKET_CLIENT.on('connect', joinRoom);
		SOCKET_CLIENT.on('GET-DATA', handleData);
		return () => {
			SOCKET_CLIENT.emit('LEAVE-USER', { roomID });
			SOCKET_CLIENT.off('connect', joinRoom);
			SOCKET_CLIENT.off('GET-DATA', handleData);
		};
	}, []);

	const trendOptions = {
		chart: {
			height: 340,
			type: 'line',
			zoom: { enabled: false },
			toolbar: { show: false },
		},
		stroke: { curve: 'smooth', width: [0, 3] },
		fill: {
			type: ['gradient', 'solid'],
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0.45,
				opacityTo: 0.05,
				stops: [0, 100],
			},
		},
		colors: ['#2F9BFF', '#FF7A59'],
		dataLabels: { enabled: false },
		xaxis: {
			categories: trendCategories,
			labels: {
				formatter: (value) => {
					if (!value) return '';
					const d = new Date(value);
					if (isNaN(d)) return value;
					return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
				},
				style: { fontSize: '11px' },
			},
		},
		yaxis: [
			{
				title: { text: 'Revenue (SAR)' },
				labels: { formatter: (v) => (v % 1 === 0 ? v : v.toFixed(2)) },
			},
			{
				opposite: true,
				title: { text: 'Orders' },
				labels: { formatter: (v) => Math.round(v) },
			},
		],
		legend: { horizontalAlign: 'left' },
		grid: { borderColor: '#e7e7e7' },
		tooltip: {
			shared: true,
			intersect: false,
			y: {
				formatter: (v, { seriesIndex }) => {
					if (seriesIndex === 0) return `${Number(v).toFixed(2)} SAR`;
					return `${v} orders`;
				},
			},
		},
		markers: { size: 4 },
	};

	// memoized so a donut redraws only when its own data changes (ApexCharts compares
	// options by value, and the functions in them would count as a change every render)
	const orderModeDonut = useDonut({
		labels: orderModeRows.map(r => r.mode),
		counts: orderModeRows.map(r => Number(r.count || 0)),
		revenues: orderModeRows.map(r => Number(r.revenue || 0)),
		sizeBy: 'count',
	});
	const paymentMethodDonut = useDonut({
		labels: paymentMethodRows.map(r => r.method),
		counts: paymentMethodRows.map(r => Number(r.count || 0)),
		revenues: paymentMethodRows.map(r => Number(r.revenue || 0)),
		sizeBy: 'revenue',
	});
	const aggregatorDonut = useDonut({
		labels: aggregatorRows.map(r => r.aggregator),
		counts: aggregatorRows.map(r => Number(r.count || 0)),
		revenues: aggregatorRows.map(r => Number(r.revenue || 0)),
		sizeBy: 'revenue',
	});
	const brandDonut = useDonut({
		labels: brandRows.map(r => r.brand),
		counts: brandRows.map(r => Number(r.orders || 0)),
		revenues: brandRows.map(r => Number(r.revenue || 0)),
		sizeBy: 'revenue',
	});

	const peakHoursOptions = {
		chart: { type: 'bar', height: DONUT_HEIGHT, toolbar: { show: false } },
		plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
		colors: ['#2F9BFF'],
		dataLabels: { enabled: false },
		xaxis: {
			categories: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`),
			labels: { style: { fontSize: '10px' } },
		},
		yaxis: { title: { text: 'Orders' }, labels: { formatter: (v) => Math.round(v) } },
		grid: { borderColor: '#e7e7e7' },
		tooltip: { y: { formatter: (v) => `${v} orders` } },
	};

	const peakHoursSeries = (() => {
		const byHour = Array(24).fill(0);
		peakHoursRows.forEach(r => {
			const h = Number(r.hour);
			if (h >= 0 && h < 24) byHour[h] = Number(r.orders || 0);
		});
		return [{ name: 'Orders', data: byHour }];
	})();

	// the APIs compare the range with the one of the same length just before it
	const rangeDays = Math.max(1, Math.round((startOfDay(range.end) - startOfDay(range.start)) / 86400000) + 1);
	const comparedWith = rangeDays === 1 ? 'vs yesterday' : `vs previous ${rangeDays} days`;

	const renderDelta = (pct) => {
		if (pct === null || pct === undefined) return null;
		const num = Number(pct);
		if (isNaN(num)) return null;
		const up = num >= 0;
		return (
			<div className='d-flex align-items-center gap-2 mt-2'>
				<span className={`kpi-delta ${up ? 'up' : 'down'}`}>
					<i className={up ? 'ti ti-trending-up' : 'ti ti-trending-down'}></i>
					{Math.abs(num).toFixed(1)}%
				</span>
				<span className='text-muted' style={{ fontSize: '12px' }}>{comparedWith}</span>
			</div>
		);
	}

	return (
		<>
			<div className='card'>
				<div className='card-header d-flex justify-content-end align-items-center gap-3'>
					<div className="date-input">
						<CustomRangepicker defaultDateFilter={todayPick} GetDateRange={(picked) => { if (picked?.start && picked?.end) setRange(picked); }} />
					</div>
					<button type="button" onClick={resetToToday} className="btn btn-primary">Reset Filter</button>
				</div>
				<div className='card-body'>
					<div className='row' data-dashboard-section='kpis'>
						{[
							{ label: 'Total Revenue', icon: 'ti ti-cash', color: '#2F9BFF', value: `${showPrice(kpis?.revenue)} SAR`, pct: kpis?.deltas?.revenue_pct },
							{ label: 'Total Orders', icon: 'ti ti-receipt', color: '#22c55e', value: kpis?.orders ?? 0, pct: kpis?.deltas?.orders_pct },
							{ label: 'Avg Order Value', icon: 'ti ti-chart-bar', color: '#a855f7', value: `${showPrice(kpis?.avg_order_value)} SAR`, pct: kpis?.deltas?.aov_pct },
							{ label: 'Refunds', icon: 'ti ti-receipt-refund', color: '#ef4444', value: kpis?.refund_count ?? 0, sub: `${showPrice(kpis?.refund_amount)} SAR refunded`, pct: kpis?.deltas?.refund_pct },
						].map((card) => (
							<div className='col-md-3' key={card.label}>
								<div className='card border kpi-card h-100'>
									<div className='card-body'>
										<div className='d-flex align-items-center gap-2'>
											<span className='kpi-icon' style={{ color: card.color, backgroundColor: `${card.color}1f` }}>
												<i className={card.icon}></i>
											</span>
											<span className='text-muted' style={{ fontSize: '13px' }}>{card.label}</span>
										</div>
										{kpiLoader ? (
											<>
												<div className='kpi-skeleton mt-3'></div>
												<div className='kpi-skeleton small'></div>
											</>
										) : (
											<>
												<h3 className='mt-2 mb-0' style={{ fontSize: '28px' }}>{card.value}</h3>
												{card.sub ? <div className='text-muted' style={{ fontSize: '12px' }}>{card.sub}</div> : null}
												{renderDelta(card.pct)}
											</>
										)}
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Each "Loading…" placeholder keeps the height of what it stands in for, so the page is
			    its full length before any data arrives and the sections below the fold stay there
			    until they're scrolled to, instead of all loading at once. */}
			<div className='card mt-4' data-dashboard-section='trend'>
				<div className='card-header d-flex justify-content-between align-items-center'>
					<h5 className='mb-0'>Revenue & Orders Trend</h5>
					<span className='text-muted' style={{ fontSize: '12px' }}>
						{trendCategories.length ? `${trendCategories[0]} — ${trendCategories[trendCategories.length - 1]}` : ''}
					</span>
				</div>
				<div className='card-body'>
					{trendLoader ? (
						<div className='d-flex align-items-center justify-content-center text-muted' style={{ minHeight: 340 }}>Loading…</div>
					) : trendCategories.length ? (
						<ReactApexChart options={trendOptions} series={trendSeries} type='line' height={340} />
					) : (
						<div className='text-center py-5 text-muted'>No data for selected range</div>
					)}
				</div>
			</div>

			<div className='row mt-4' data-dashboard-section='splits'>
				<div className='col-md-4'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Sales by Order Mode</h5>
						</div>
						<div className='card-body'>
							{orderModeLoader ? (
								<div className='d-flex align-items-center justify-content-center text-muted' style={{ minHeight: DONUT_HEIGHT }}>Loading…</div>
							) : orderModeRows.length ? (
								<ReactApexChart
									options={orderModeDonut.options}
									series={orderModeDonut.series}
									type='donut'
									height={DONUT_HEIGHT}
								/>
							) : (
								<div className='text-center py-5 text-muted'>No data</div>
							)}
						</div>
					</div>
				</div>
				<div className='col-md-4'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Sales by Aggregator</h5>
						</div>
						<div className='card-body'>
							{aggregatorLoader ? (
								<div className='d-flex align-items-center justify-content-center text-muted' style={{ minHeight: DONUT_HEIGHT }}>Loading…</div>
							) : aggregatorRows.length ? (
								<ReactApexChart
									options={aggregatorDonut.options}
									series={aggregatorDonut.series}
									type='donut'
									height={DONUT_HEIGHT}
								/>
							) : (
								<div className='text-center py-5 text-muted'>No data</div>
							)}
						</div>
					</div>
				</div>
				<div className='col-md-4'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Sales by Payment Method</h5>
						</div>
						<div className='card-body'>
							{paymentMethodLoader ? (
								<div className='d-flex align-items-center justify-content-center text-muted' style={{ minHeight: DONUT_HEIGHT }}>Loading…</div>
							) : paymentMethodRows.length ? (
								<ReactApexChart
									options={paymentMethodDonut.options}
									series={paymentMethodDonut.series}
									type='donut'
									height={DONUT_HEIGHT}
								/>
							) : (
								<div className='text-center py-5 text-muted'>No data</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className='row mt-4'>
				<div className='col-md-4' data-dashboard-section='brands'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Sales by Brand</h5>
						</div>
						<div className='card-body'>
							{brandLoader ? (
								<div className='d-flex align-items-center justify-content-center text-muted' style={{ minHeight: DONUT_HEIGHT }}>Loading…</div>
							) : brandRows.length ? (
								<ReactApexChart
									options={brandDonut.options}
									series={brandDonut.series}
									type='donut'
									height={DONUT_HEIGHT}
								/>
							) : (
								<div className='text-center py-5 text-muted'>No data</div>
							)}
						</div>
					</div>
				</div>
				<div className='col-md-8' data-dashboard-section='peakHours'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Peak Hours</h5>
							<small className='text-muted'>Orders by hour-of-day across the selected range</small>
						</div>
						<div className='card-body'>
							{peakHoursLoader ? (
								<div className='d-flex align-items-center justify-content-center text-muted' style={{ minHeight: DONUT_HEIGHT }}>Loading…</div>
							) : peakHoursRows.length ? (
								<ReactApexChart options={peakHoursOptions} series={peakHoursSeries} type='bar' height={DONUT_HEIGHT} />
							) : (
								<div className='text-center py-5 text-muted'>No data</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className='row mt-4' data-dashboard-section='topLists'>
				<div className='col-md-4'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Top Menu Items</h5>
						</div>
						<div className='card-body p-0'>
							<div className='table-responsive'>
								<table className='table mb-0'>
									<thead>
										<tr>
											<th>Item</th>
											<th className='text-end'>Qty</th>
											<th className='text-end'>Revenue</th>
										</tr>
									</thead>
									<tbody>
										{topMenuItemsLoader ? (
											<tr><td colSpan={3} className='text-center align-middle text-muted' style={{ height: 360 }}>Loading…</td></tr>
										) : topMenuItems.length ? topMenuItems.map((row, i) => (
											<tr key={row.menu_item_id || i}>
												<td>{row.name || '-'}</td>
												<td className='text-end'>{row.qty || 0}</td>
												<td className='text-end'>{showPrice(row.revenue)} SAR</td>
											</tr>
										)) : (
											<tr><td colSpan={3} className='text-center py-4 text-muted'>No data</td></tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className='col-md-4'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Top Outlets</h5>
						</div>
						<div className='card-body p-0'>
							<div className='table-responsive'>
								<table className='table mb-0'>
									<thead>
										<tr>
											<th>Outlet</th>
											<th className='text-end'>Orders</th>
											<th className='text-end'>Revenue</th>
										</tr>
									</thead>
									<tbody>
										{topOutletsLoader ? (
											<tr><td colSpan={3} className='text-center align-middle text-muted' style={{ height: 360 }}>Loading…</td></tr>
										) : topOutlets.length ? topOutlets.map((row, i) => (
											<tr key={row.outlet_id || i}>
												<td>{row.outlet ? `${row.outlet}${row.outlet_short_code ? ` (${row.outlet_short_code})` : ''}` : '-'}</td>
												<td className='text-end'>{row.orders || 0}</td>
												<td className='text-end'>{showPrice(row.revenue)} SAR</td>
											</tr>
										)) : (
											<tr><td colSpan={3} className='text-center py-4 text-muted'>No data</td></tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className='col-md-4'>
					<div className='card h-100'>
						<div className='card-header'>
							<h5 className='mb-0'>Top Customers</h5>
						</div>
						<div className='card-body p-0'>
							<div className='table-responsive'>
								<table className='table mb-0'>
									<thead>
										<tr>
											<th>Customer</th>
											<th className='text-end'>Orders</th>
											<th className='text-end'>Spend</th>
										</tr>
									</thead>
									<tbody>
										{topCustomersLoader ? (
											<tr><td colSpan={3} className='text-center align-middle text-muted' style={{ height: 360 }}>Loading…</td></tr>
										) : topCustomers.length ? topCustomers.map((row, i) => (
											<tr key={row.customer_id || i}>
												<td>{row.name || '-'}</td>
												<td className='text-end'>{row.orders || 0}</td>
												<td className='text-end'>{showPrice(row.spend)} SAR</td>
											</tr>
										)) : (
											<tr><td colSpan={3} className='text-center py-4 text-muted'>No data</td></tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* mounted the first time it's reached, so its lists load only then */}
			<div data-dashboard-section='recent' style={{ minHeight: '1px' }}>
				{showRecentLists && (
					<DashboardList dateFilter={{ start_date: buildPayload().start_date, end_date: buildPayload().end_date }} />
				)}
			</div>
		</>
	)
}

export default Dashboard
